/**
 * MR MAIN MAN – referral tracking
 *
 * Doel:
 * - Leest ?refc= uit de binnenkomst-URL.
 * - Houdt de code zichtbaar in alle interne webshop-URL's.
 * - Bewaart de code gedurende de browsersessie.
 * - Slaat de code op als Shopify cart attribute: refc.
 *
 * Voorbeeld:
 * /nl/pages/ontdek-mr-main-man?refc=ABC123
 */

(() => {
  'use strict';

  const PARAM_NAME = 'refc';
  const STORAGE_KEY = 'mrmm_refc';
  const CART_SYNC_KEY = 'mrmm_refc_cart_synced';

  /**
   * Alleen eenvoudige, veilige referralcodes accepteren.
   * Toegestaan: letters, cijfers, liggend streepje en underscore.
   */
  function sanitizeReferralCode(value) {
    if (typeof value !== 'string') return null;

    const cleaned = value.trim();

    if (!cleaned || cleaned.length > 100) return null;
    if (!/^[A-Za-z0-9_-]+$/.test(cleaned)) return null;

    return cleaned;
  }

  function getReferralFromCurrentUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      return sanitizeReferralCode(params.get(PARAM_NAME));
    } catch (error) {
      console.warn('[MRMM referral] URL kon niet worden gelezen.', error);
      return null;
    }
  }

  function saveReferralCode(code) {
    try {
      sessionStorage.setItem(STORAGE_KEY, code);
    } catch (error) {
      console.warn(
        '[MRMM referral] Referralcode kon niet worden opgeslagen.',
        error
      );
    }
  }

  function getSavedReferralCode() {
    try {
      return sanitizeReferralCode(sessionStorage.getItem(STORAGE_KEY));
    } catch (error) {
      return null;
    }
  }

  /**
   * Zorgt dat refc ook zichtbaar wordt wanneer iemand binnen dezelfde
   * browsersessie op een pagina terechtkomt waar de parameter ontbreekt.
   */
  function addReferralToCurrentUrl(code) {
    try {
      const url = new URL(window.location.href);

      if (url.searchParams.get(PARAM_NAME) === code) return;

      url.searchParams.set(PARAM_NAME, code);

      window.history.replaceState(
        window.history.state,
        document.title,
        url.toString()
      );
    } catch (error) {
      console.warn(
        '[MRMM referral] Referralcode kon niet aan de huidige URL worden toegevoegd.',
        error
      );
    }
  }

  function isInternalShopLink(url) {
    if (url.origin !== window.location.origin) return false;

    /*
     * Checkout en accountprocessen niet herschrijven.
     * De referralcode staat voor checkout al in het Shopify-winkelmandje.
     */
    const excludedPaths = [
      '/checkout',
      '/checkouts/',
      '/account',
      '/admin'
    ];

    return !excludedPaths.some((path) => url.pathname.startsWith(path));
  }

  function shouldIgnoreLink(link) {
    if (!(link instanceof HTMLAnchorElement)) return true;

    const href = link.getAttribute('href');

    if (!href) return true;
    if (link.hasAttribute('download')) return true;

    return (
      href.startsWith('#') ||
      href.startsWith('mailto:') ||
      href.startsWith('tel:') ||
      href.startsWith('sms:') ||
      href.startsWith('javascript:')
    );
  }

  function addReferralToLink(link, code) {
    if (shouldIgnoreLink(link)) return;

    try {
      const url = new URL(link.href, window.location.origin);

      if (!isInternalShopLink(url)) return;

      url.searchParams.set(PARAM_NAME, code);

      /*
       * Gebruik een relatieve URL voor interne Shopify-links.
       * Zo blijven domein en locale netjes behouden.
       */
      link.setAttribute(
        'href',
        `${url.pathname}${url.search}${url.hash}`
      );
    } catch (error) {
      console.warn(
        '[MRMM referral] Een interne link kon niet worden aangepast.',
        error
      );
    }
  }

  function updateAllInternalLinks(code, root = document) {
    const links = root.querySelectorAll
      ? root.querySelectorAll('a[href]')
      : [];

    links.forEach((link) => addReferralToLink(link, code));
  }

  /**
   * Voeg refc als verborgen veld toe aan interne GET-formulieren,
   * bijvoorbeeld zoekformulieren.
   */
  function updateGetForms(code, root = document) {
    const forms = root.querySelectorAll
      ? root.querySelectorAll('form')
      : [];

    forms.forEach((form) => {
      const method = (form.getAttribute('method') || 'get').toLowerCase();

      if (method !== 'get') return;

      try {
        const action = new URL(
          form.getAttribute('action') || window.location.href,
          window.location.origin
        );

        if (!isInternalShopLink(action)) return;

        let input = form.querySelector(
          `input[name="${PARAM_NAME}"]`
        );

        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = PARAM_NAME;
          form.appendChild(input);
        }

        input.value = code;
      } catch (error) {
        console.warn(
          '[MRMM referral] Een formulier kon niet worden aangepast.',
          error
        );
      }
    });
  }

  /**
   * Bewaar refc als cart attribute.
   * Hierdoor kan de code met de winkelwagen en bestelling meegaan.
   */
  async function saveReferralToShopifyCart(code) {
    try {
      const alreadySynced =
        sessionStorage.getItem(CART_SYNC_KEY) === code;

      if (alreadySynced) return;

      const shopRoot =
        window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root
          ? window.Shopify.routes.root
          : '/';

      const response = await fetch(`${shopRoot}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          attributes: {
            refc: code
          }
        })
      });

      if (!response.ok) {
        throw new Error(
          `Shopify cart update mislukt: ${response.status}`
        );
      }

      sessionStorage.setItem(CART_SYNC_KEY, code);
    } catch (error) {
      console.warn(
        '[MRMM referral] Referralcode kon niet aan de winkelwagen worden toegevoegd.',
        error
      );
    }
  }

  function observeDynamicContent(code) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;

          if (node.matches('a[href]')) {
            addReferralToLink(node, code);
          }

          updateAllInternalLinks(code, node);
          updateGetForms(code, node);
        });
      });
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Extra vangnet voor links die door apps of het thema vlak vóór
   * de klik worden aangepast.
   */
  function monitorClicks(code) {
    document.addEventListener(
      'click',
      (event) => {
        const link = event.target.closest('a[href]');

        if (link) {
          addReferralToLink(link, code);
        }
      },
      true
    );
  }

  function initializeReferralTracking() {
    const urlCode = getReferralFromCurrentUrl();

    if (urlCode) {
      saveReferralCode(urlCode);
    }

    const referralCode = urlCode || getSavedReferralCode();

    if (!referralCode) return;

    addReferralToCurrentUrl(referralCode);
    updateAllInternalLinks(referralCode);
    updateGetForms(referralCode);
    monitorClicks(referralCode);
    observeDynamicContent(referralCode);
    saveReferralToShopifyCart(referralCode);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeReferralTracking,
      { once: true }
    );
  } else {
    initializeReferralTracking();
  }
})();